import type { ColumnSchema } from './types';

/** 常用列宽（px） */
export const COL_WIDTH = {
  index: 56,
  selection: 50,
  project: 148,
  mould: 200,
  /** 问题 / 说明 / 任务等长文本列默认宽度 */
  longText: 240,
  longTextMin: 180,
  date: 112,
  /** 含表头「发起试模时间」等 6 字 + 单元格日期时间 */
  dateTime: 168,
  dateTimeMin: 152,
  status: 100,
  /** 待办「状态」列（放产 · 终态归档 / 验证进度），须在 ColumnSchema 中显式 width */
  todoStatus: 200,
  /** 待办问题 / 我的问题 / 任务列固定宽度（flex: false，避免内容少时弹性撑满显空） */
  todoIssues: 200,
  /** 操作列最小宽度（表头「操作」+ 单个紧凑按钮）；列宽随内容撑开，不设固定 width */
  actionMin: 88,
  text: 120,
  image: 100,
} as const;

export interface ResolvedColSizing {
  width?: string | number;
  minWidth?: string | number;
  className?: string;
}

function normalizeLabel(label?: string): string {
  return label?.replace(/\s/g, '') ?? '';
}

/** 操作列：type=action 或 label=操作 */
export function isActionColumn(col: ColumnSchema): boolean {
  return col.type === 'action' || normalizeLabel(col.label) === '操作';
}

/**
 * 列固定策略：
 * - fixed=false → 不固定（可覆盖操作列默认）
 * - fixed=true / 'left' → 左侧固定
 * - fixed='right' → 右侧固定
 * - 操作列未配置时默认右侧固定
 */
export function resolveColFixed(col: ColumnSchema): boolean | 'left' | 'right' | undefined {
  const { fixed } = col;
  if (fixed === false) return undefined;
  if (fixed === true || fixed === 'left') return 'left';
  if (fixed === 'right') return 'right';
  if (isActionColumn(col)) return 'right';
  return undefined;
}

function toNumber(value: string | number | undefined): number | undefined {
  if (value == null) return undefined;
  const n = typeof value === 'number' ? value : Number.parseInt(String(value), 10);
  return Number.isFinite(n) ? n : undefined;
}

/** 日期 / 时间类列（表头或 type=date） */
export function isDateTimeColumn(col: ColumnSchema): boolean {
  if (col.type === 'date') return true;
  const n = normalizeLabel(col.label);
  return /时间|日期|交期/.test(n);
}

/** 可能多行展示的长文本业务列 */
export function isLongTextColumn(col: ColumnSchema): boolean {
  const n = normalizeLabel(col.label);
  if (n === '问题' || n === '我的问题' || n.includes('问题描述')) return true;
  if (n === '说明' || n.endsWith('说明')) return true;
  if (n === '任务' || (n.includes('任务') && !n.includes('任务数'))) return true;
  const prop = (col as { prop?: string }).prop;
  return ['desc', 'description', 'note', 'task', 'issues', 'result'].includes(prop ?? '');
}

function shouldFlexColumn(col: ColumnSchema): boolean {
  if (isActionColumn(col)) return false;
  if (col.flex === false) return false;
  if (col.flex === true) return true;
  // 仅 minWidth、无 width → 弹性列，均分剩余宽度（开模推进阶段列等）
  if (col.width == null && col.minWidth != null) return true;
  return isLongTextColumn(col);
}

/** type=action 列：按按钮文案估算宽度（图标 + 文字 link 按钮） */
export function estimateActionColMinWidth(
  buttons: { label: string; icon?: unknown }[],
  maxVisible = 3
): number {
  const visible = buttons.slice(0, maxVisible);
  let w = 24; // 单元格左右留白
  visible.forEach((btn, i) => {
    if (i > 0) w += 4;
    if (btn.icon !== false) w += 16;
    w += btn.label.length * 14;
    w += 12;
  });
  if (buttons.length > maxVisible) w += 52;
  return Math.max(COL_WIDTH.actionMin, w);
}

/** 扫描全表各行可见按钮，取最宽一行作为操作列 width（minWidth 为下限） */
export function resolveActionColumnWidth(col: ColumnSchema, data: Record<string, any>[]): number {
  const sizing = resolveColSizing(col);
  const floor = toNumber(sizing.minWidth) ?? COL_WIDTH.actionMin;
  if (!isActionColumn(col)) return floor;

  let maxW = floor;

  if (col.actionLabels) {
    for (const row of data) {
      const labels = col.actionLabels(row);
      if (!labels.length) continue;
      maxW = Math.max(
        maxW,
        estimateActionColMinWidth(
          labels.map((label) => ({ label, icon: true })),
          labels.length
        )
      );
    }
    return maxW;
  }

  if (col.type === 'action') {
    const actionCol = col as Extract<ColumnSchema, { type: 'action' }>;
    const maxVisible = actionCol.maxVisible ?? 3;
    const scan = data.length ? data : [null];
    for (const row of scan) {
      const buttons = row ? actionCol.buttons.filter((b) => !b.hidden?.(row)) : actionCol.buttons;
      maxW = Math.max(
        maxW,
        estimateActionColMinWidth(
          buttons.map((b) => ({ label: b.label, icon: b.icon ?? true })),
          maxVisible
        )
      );
    }
    return maxW;
  }

  return floor;
}

/**
 * 解析列宽：
 * - 仅 minWidth 的列默认弹性，均分剩余宽度使表格 100% 铺满
 * - flex=false 时仅 minWidth 视为固定宽度（width = minWidth）
 * - 时间列宽度过窄时抬升到可完整展示表头 + 日期时间
 * - 未配置宽度的常见业务列给出合理默认值
 */
export function resolveColSizing(col: ColumnSchema): ResolvedColSizing {
  if (col.type === 'group') {
    return { className: col.className };
  }
  const flex = shouldFlexColumn(col);
  let width = col.width;
  let minWidth = col.minWidth;
  const classNames: string[] = [];
  if (col.className) classNames.push(col.className);

  // 显式禁止弹性：仅 minWidth → 固定为同宽
  if (col.flex === false && width == null && minWidth != null) {
    width = minWidth;
  }

  // 时间列：表头 4–6 字 + YYYY-MM-DD HH:mm
  if (isDateTimeColumn(col) && !flex) {
    const w = toNumber(width);
    if (w == null || w < COL_WIDTH.dateTimeMin) {
      width = COL_WIDTH.dateTime;
    }
    minWidth = minWidth ?? COL_WIDTH.dateTimeMin;
  }

  // 操作列：不设固定 width，仅 minWidth 保底，列宽随按钮内容自然撑开
  if (isActionColumn(col)) {
    width = undefined;
    if (minWidth == null) minWidth = COL_WIDTH.actionMin;
  }

  if (width == null && minWidth == null) {
    const n = normalizeLabel(col.label);

    if (col.type === 'index') {
      width = COL_WIDTH.index;
    } else if (col.type === 'selection') {
      width = COL_WIDTH.selection;
    } else if (isActionColumn(col)) {
      minWidth = COL_WIDTH.actionMin;
    } else if (n.includes('项目')) {
      width = COL_WIDTH.project;
    } else if (n.includes('模具') && !/模具(数|类型)/.test(n)) {
      width = COL_WIDTH.mould;
      minWidth = 160;
    } else if (isLongTextColumn(col)) {
      minWidth = COL_WIDTH.longTextMin;
    } else if (/状态|类型|轮次|齐备率/.test(n)) {
      width = COL_WIDTH.status;
    } else if (col.type === 'image') {
      width = COL_WIDTH.image;
    } else if (isDateTimeColumn(col)) {
      width = COL_WIDTH.dateTime;
      minWidth = COL_WIDTH.dateTimeMin;
    } else {
      minWidth = COL_WIDTH.text;
    }
  }

  return {
    width,
    minWidth,
    className: classNames.length ? classNames.join(' ') : undefined,
  };
}
